import { Locale, locales } from "./types"

export function getLocalePath(locale: Locale, pattern: string): string {
  return `/${locale}${pattern}`
}

export function buildPath<Params extends Record<string, unknown>>(
  pattern: string,
  params: Params
): string {
  let path = pattern
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, String(value))
  }
  return path
}

export function buildLocalePath<Params extends Record<string, unknown>>(
  locale: Locale,
  pattern: string,
  params: Params
): string {
  return getLocalePath(locale, buildPath(pattern, params))
}

export function patternToRegex(pattern: string, options: { withLocale?: boolean } = {}): RegExp {
  let regexPattern = pattern.replace(/:([^/]+)/g, "([^/]+)")
  if (options.withLocale) {
    const localePattern = `(${locales.join("|")})`
    regexPattern = `/${localePattern}${regexPattern}`
  }
  return new RegExp(`^${regexPattern}$`)
}

export function extractParamsFromMatch<Params extends Record<string, unknown>>(
  match: RegExpExecArray,
  paramTypes: { [K in keyof Params]: (s: string) => Params[K] },
  options: { withLocale?: boolean } = {}
): Params {
  const startIndex = options.withLocale ? 2 : 1
  const paramKeys = Object.keys(paramTypes) as (keyof Params)[]
  const result = {} as Params
  
  paramKeys.forEach((key, index) => {
    const value = match[startIndex + index]
    result[key] = paramTypes[key](value)
  })
  
  return result
}

/**
 * ルートパターンをiOS Universal Links用のワイルドカードパターンに変換する
 * 例: "/users/:userId" → "/users/*"
 */
export function patternToUniversalLinkPath(pattern: string): string {
  return pattern.replace(/:([^/]+)/g, "*")
}

/**
 * ルート定義オブジェクトから全てのパターンを再帰的に抽出する
 */
export function extractAllPatterns(definitions: unknown): string[] {
  const patterns: string[] = []

  function traverse(obj: unknown): void {
    if (obj === null || typeof obj !== "object") return

    const record = obj as Record<string, unknown>
    if ("pattern" in record && typeof record.pattern === "string" && record.pattern.startsWith("/")) {
      patterns.push(record.pattern)
    }

    for (const value of Object.values(record)) {
      if (typeof value === "object" && value !== null) {
        traverse(value)
      }
    }
  }

  traverse(definitions)
  return patterns
}

/**
 * ルート定義からiOS Universal Links用のパスリストを生成する
 * 各パターンに対して、ロケールあり(/wildcard/{pattern})とロケールなし(/{pattern})の両方を生成
 */
export function generateUniversalLinkPaths(definitions: unknown): string[] {
  const patterns = extractAllPatterns(definitions)
  const paths: string[] = []

  for (const pattern of patterns) {
    const wildcardPath = patternToUniversalLinkPath(pattern)
    paths.push(`/*${wildcardPath}`)
    paths.push(wildcardPath)
  }

  return paths
}
